import styled, { css, keyframes } from 'styled-components'

const focusRing = css`
  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }
`

export const StyledList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-3);
  min-height: 0;
`

export const StyledListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--m-spacing-2);
`

export const StyledNewChat = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--m-spacing-2);
  padding: var(--m-spacing-2) var(--m-spacing-3);
  border: var(--m-border-thin) solid rgb(var(--m-color-border));
  border-radius: var(--m-border-radius-m);
  background-color: rgb(var(--m-color-background-primary));
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-primary-100));
  cursor: pointer;

  &:hover {
    background-color: rgb(var(--m-color-background-highlight));
  }

  ${focusRing}
`

export const StyledRows = styled.ul`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-1);
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
`

/** Rename and delete show on hover and keyboard focus; touch screens always show them. */
export const StyledRowActions = styled.div`
  display: flex;
  gap: var(--m-spacing-1);
  padding-right: var(--m-spacing-2);
  opacity: 0;

  @media (hover: none) {
    opacity: 1;
  }
`

export const StyledRow = styled.li<{ $active: boolean }>`
  display: flex;
  align-items: center;
  border-radius: var(--m-border-radius-m);
  background-color: ${({ $active }) =>
    $active ? 'rgb(var(--m-color-background-highlight))' : 'transparent'};

  &:hover,
  &:focus-within {
    background-color: rgb(var(--m-color-background-highlight));
  }

  &:hover ${StyledRowActions}, &:focus-within ${StyledRowActions} {
    opacity: 1;
  }
`

export const StyledSelect = styled.button`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: var(--m-spacing-2) var(--m-spacing-3);
  border: 0;
  border-radius: var(--m-border-radius-m);
  background: none;
  text-align: left;
  cursor: pointer;

  ${focusRing}
`

export const StyledTitle = styled.span<{ $active: boolean }>`
  overflow: hidden;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: ${({ $active }) =>
    $active ? 'var(--m-font-weight-bold)' : 'var(--m-font-weight-regular)'};
  line-height: var(--m-line-height-5);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgb(var(--m-color-primary-100));
`

export const StyledTime = styled.time`
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  color: rgb(var(--m-color-text-placeholder));
`

export const StyledIconButton = styled.button`
  position: relative;
  display: inline-flex;
  padding: var(--m-spacing-1);
  border: 0;
  border-radius: var(--m-border-radius-s);
  background: none;
  color: rgb(var(--m-color-text-tertiary));
  cursor: pointer;

  &:hover {
    color: rgb(var(--m-color-primary-100));
  }

  /* On touch the buttons are always shown: a bigger target, 44px tall without a taller row. */
  @media (hover: none) {
    padding: var(--m-spacing-2);

    &::after {
      content: '';
      position: absolute;
      inset: min(0px, (100% - 44px) / 2) 0;
    }
  }

  ${focusRing}
`

export const StyledNote = styled.p`
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-tertiary));
`

export const StyledTextButton = styled.button`
  align-self: flex-start;
  padding: var(--m-spacing-1) 0;
  border: 0;
  background: none;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-text-link));
  cursor: pointer;

  ${focusRing}
`

const shimmer = keyframes`
  0% { background-position: -240px 0; }
  100% { background-position: 240px 0; }
`

export const StyledSkeleton = styled.div`
  height: 40px;
  border-radius: var(--m-border-radius-m);
  background: linear-gradient(
    90deg,
    rgb(var(--m-color-background-secondary)) 25%,
    rgb(var(--m-color-background-tertiary)) 37%,
    rgb(var(--m-color-background-secondary)) 63%
  );
  background-size: 480px 100%;
  animation: ${shimmer} 1.4s ease infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
